// src/contexts/FinanceAccountContext.js
//
// Holds the globally selected CRM Account for the Finance Portal.
// All finance list pages (customers, vendors, invoices, bills, etc.)
// read the active account via `useFinanceAccount()` and pass its `id`
// as the `crm_account_id` query param to their list endpoints.
//
// Persistence: id is stored in localStorage under `finance.selectedAccountId`,
// then rehydrated to a full account object on mount (single API call).

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';

const STORAGE_KEY = 'finance.selectedAccountId';

const FinanceAccountContext = createContext({
    selectedAccount: null,
    setSelectedAccount: () => {},
    clearAccount: () => {},
    recentAccounts: [],
    refreshRecent: () => {},
    accountParam: undefined,
    isReady: false,
});

export function FinanceAccountProvider({ children }) {
    const [selectedAccount, setSelectedAccountState] = useState(null);
    const [recentAccounts, setRecentAccounts] = useState([]);
    const [isReady, setIsReady] = useState(false);

    const refreshRecent = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/crm-accounts/recent', { params: { limit: 5 } });
            if (res.data?.success) setRecentAccounts(res.data.data || []);
        } catch (_) { /* silent */ }
    }, []);

    // Install a global request interceptor so EVERY request to /api/finance/*
    // automatically carries the selected account as `crm_account_id` — on all
    // HTTP methods (GET, POST, PUT, PATCH, DELETE). It is added to the query
    // params, and also stamped into JSON bodies on write methods so backend
    // handlers that read it from either place pick it up.
    //
    // The id is read from localStorage (not React state) so requests fired
    // during the first render — before the context has finished hydrating
    // the selected account — still carry the correct id. Otherwise page
    // components that fetch on mount would briefly see unfiltered data.
    //
    // Excluded paths: the account picker itself, public portal, and any URL
    // that already specifies its own crm_account_id.
    useEffect(() => {
        const id = axiosInstance.interceptors.request.use((config) => {
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                const accountId = stored ? parseInt(stored, 10) : null;
                if (!accountId) return config;
                const url = (config.url || '');
                if (!url.includes('/api/finance/')) return config;
                if (url.includes('/api/finance/crm-accounts')) return config;
                if (url.includes('/api/finance/portal/')) return config;

                config.params = config.params || {};
                if (config.params.crm_account_id == null) {
                    config.params.crm_account_id = accountId;
                }

                const method = (config.method || 'get').toLowerCase();
                const isWrite = method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
                if (isWrite && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
                    if (config.data.crm_account_id == null) {
                        config.data.crm_account_id = accountId;
                    }
                } else if (isWrite && config.data instanceof FormData) {
                    if (!config.data.has('crm_account_id')) {
                        config.data.append('crm_account_id', accountId);
                    }
                }
            } catch (_) { /* never break a request */ }
            return config;
        });
        return () => axiosInstance.interceptors.request.eject(id);
    }, []);

    // Rehydrate persisted account id on mount
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const id = localStorage.getItem(STORAGE_KEY);
            if (id) {
                try {
                    const res = await axiosInstance.get(`/api/finance/crm-accounts/${id}`);
                    if (!cancelled && res.data?.success) {
                        setSelectedAccountState(res.data.data);
                    } else if (!cancelled) {
                        localStorage.removeItem(STORAGE_KEY);
                    }
                } catch (_) {
                    if (!cancelled) localStorage.removeItem(STORAGE_KEY);
                }
            }
            await refreshRecent();
            if (!cancelled) setIsReady(true);
        })();
        return () => { cancelled = true; };
    }, [refreshRecent]);

    const setSelectedAccount = useCallback(async (account) => {
        setSelectedAccountState(account);
        if (account?.id) {
            localStorage.setItem(STORAGE_KEY, String(account.id));
            // Best-effort: tell backend this is now a recently used account
            try {
                await axiosInstance.post('/api/finance/crm-accounts/recent', { crm_account_id: account.id });
                refreshRecent();
            } catch (_) { /* silent */ }
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [refreshRecent]);

    const clearAccount = useCallback(() => {
        setSelectedAccountState(null);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    const value = useMemo(() => ({
        selectedAccount,
        setSelectedAccount,
        clearAccount,
        recentAccounts,
        refreshRecent,
        // Convenience: spread into axios params object — undefined when no account is selected,
        // so URLs don't get a `crm_account_id=` empty param.
        accountParam: selectedAccount?.id ? { crm_account_id: selectedAccount.id } : undefined,
        isReady,
    }), [selectedAccount, setSelectedAccount, clearAccount, recentAccounts, refreshRecent, isReady]);

    return (
        <FinanceAccountContext.Provider value={value}>
            {children}
        </FinanceAccountContext.Provider>
    );
}

export function useFinanceAccount() {
    return useContext(FinanceAccountContext);
}
